import { FC, useState } from 'react';
import EmailInput from '@components/FormFields/EmailInput';
import Spacer from '@components/Spacer';
import InputField from '@components/FormFields/InputField';
import Buttons from '@components/Buttons/Buttons';
import { useUser } from '@hooks';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import s from '@components/CartUserInfoForm/CartUserInfoForm.module.scss';
import PhoneInput from '@components/FormFields/PhoneInputNew';
import { logError, Severity } from '@providers/ErrorTracking';

type Props = {
  creatorSlug: string;
  initialEmail: string | null;
  initialName: string | null;
  setPersonId: (newId: string) => void;
  setUserName: (newName: string) => void;
  setUserEmail: (newEmail: string) => void;
  onFinish?: () => void;
};

const CartUserInfoForm: FC<Props> = ({
  creatorSlug,
  initialEmail,
  initialName,
  setPersonId,
  setUserName,
  setUserEmail,
  onFinish,
}) => {
  const [_, __, setUserByToken] = useUser();
  const [name, setName] = useState(initialName || '');
  const [email, setEmail] = useState(initialEmail || '');
  const [phoneNumber, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [personError, setPersonError] = useState<string | null>(null);
  const isButtonDisabled = !name || !email || Boolean(emailError) || Boolean(phoneError);
  const { getData: findOrCreatePerson } = useNoodleApi(tsClient.findOrCreatePerson);
  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    setUserName(name);
    setUserEmail(email);
    const { data } = await findOrCreatePerson({
      creatorSlug,
      identifier: email,
      name,
      phoneNumber,
    });
    if (data?.token) {
      setUserByToken(data.token);
    }
    if (data?.person) {
      setPersonId(data.person.id);
      onFinish?.();
    } else {
      setPersonError('Unable to find/create person');
      logError(new Error('Unable to find/create person'), { level: Severity.Fatal, tags: { flow: 'purchase' } });
    }
    setIsLoading(false);
  };
  return (
    <div className={s.container}>
      <p className='body-sm-bold'>Enter your information:</p>
      <Spacer size={20} />
      {personError && (
        <div className={s.errorMessage}>
          We were unable to create your account. Please try again or <a href={'mailto:support@noodle.shop'}>contact support</a>.
        </div>
      )}
      <InputField
        id="name"
        label="Name"
        name="name"
        values={{ name }}
        placeholder="Enter your name"
        onChange={setName}
        autoComplete={'name'}
      />
      <Spacer size={16}/>
      <EmailInput
        id='email'
        value={email}
        onChange={setEmail}
        onError={setEmailError}
        label="Email"
        required
        placeholder="Enter your email address"
      />
      <Spacer size={16} />
      <PhoneInput
        id='phone'
        value={phoneNumber}
        label='Phone number'
        placeholder='Enter your phone number (optional)'
        onChange={setPhone}
        onError={setPhoneError}
        required={false}
      />
      <Spacer size={16} />
      <Buttons
        disabled={isButtonDisabled}
        isSecondary
        isFetching={isLoading}
        isFullWidth
        onClick={handleSubmit}
      >
        Next
      </Buttons>
    </div>
  );
};

export default CartUserInfoForm;
